import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { buildApiUrl } from '../../config/api';
import {
  statusOf, weekSummary, rosterHeadline, dueTodayCount, readinessSummary,
} from '../rosterOverview';
import '../../styles/RosterOverview.css';

// The coach's worklist — who needs chasing this morning.
//
// # WHY THIS IS A COMPONENT AND NOT STILL INLINE IN COACHING
//
// It rendered inside Coaching.js, below a page header and between three admin
// forms: add an athlete, create a group, give out a programme. Those are things
// a coach does occasionally and deliberately. This is the thing they open the
// app for, and it was reachable only by navigating past the administration of
// the roster to reach the state of it.
//
// It now renders on /home instead, so a coach lands on the question
// routes/rollup.go was written to answer — "who trained, who has not, and who
// needs chasing" — and /coaching is left as the admin surface it already
// mostly was.
//
// # WHY IT LIVES IN A SUBDIRECTORY
//
// Not style. The pure module it draws on is components/rosterOverview.js, and
// the repo's convention would pair it with components/RosterOverview.js — but
// the filesystem this is developed on is case-insensitive, so those two names
// are ONE file and the component silently overwrites the module. It happened
// once while this was being written; the module survived only because it was
// committed. components/training/ReadinessCard.js — a component in a
// subdirectory whose pure module is components/readiness.js — is the existing
// answer to the same problem, and this follows it.
//
// # WHY IT FETCHES ITSELF
//
// Home.js short-circuits to MyTrainingHome before this renders, so an athlete
// never mounts it and never pays for the query. /roster/overview is N+1-shaped
// per athlete, and making Home fetch it up front would have put that cost on
// every athlete's landing page to render something they are never shown.
//
// The same reasoning as ReadinessCard, which MyTrainingHome mounts the same way.
const RosterOverview = () => {
  const { token } = useAuth();
  const [overview, setOverview] = useState([]);

  const authConfig = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await axios.get(buildApiUrl('/roster/overview'), authConfig());
        if (live) setOverview(data?.athletes ?? []);
      } catch {
        // A roll-up that cannot be loaded must not cost the coach the rest of
        // their home. It renders nothing and the cards below it are untouched —
        // the same non-fatal treatment this fetch had on /coaching, where the
        // defect it prevents was a failed overview taking the assign form down
        // with it.
      }
    })();
    return () => { live = false; };
  }, [authConfig]);

  // # WHY AN EMPTY ROLL-UP RENDERS NOTHING RATHER THAN ITS OWN EMPTY STATE
  //
  // rosterHeadline() can say "Nobody on your roster yet", and on /coaching that
  // was the right thing to print: the roster admin is on the same page, so the
  // sentence sits next to the form that fixes it.
  //
  // Here it would be the first thing on /home with no such form beside it. And
  // it should be nearly unreachable anyway — audienceFrom only returns COACH
  // when the roster is non-empty, so this state means /athletes returned rows
  // and /roster/overview did not: a partial failure, which rollup.go produces
  // by skipping an athlete whose user row will not load. An empty panel is the
  // wrong way to report that; the cards below are still usable.
  if (overview.length === 0) return null;

  const due = dueTodayCount(overview);

  return (
    <section className="card roster-panel">
      <h2 className="section-title">{rosterHeadline(overview)}</h2>
      <p className="meta-copy">
        {due > 0
          ? `${due} session${due === 1 ? '' : 's'} due across the squad today.`
          : 'Nothing due across the squad today.'}
      </p>

      {/* Ordered for triage by the server: whoever needs attention first,
          then by how long they have been quiet. */}
      <ul className="roster-list">
        {overview.map((row) => {
          const status = statusOf(row);
          const week = weekSummary(row);
          const readiness = readinessSummary(row);
          return (
            <li key={row.athleteId}>
              <span>
                <strong>{row.displayName || row.username}</strong>
                <div className={`roster-status roster-status-${status.tone}`}>
                  {status.text}
                </div>
                {/*
                  meta-copy, not `muted`: there is no flat `.muted` rule in this
                  codebase or in @loenhart/design — every definition is scoped
                  under a screen (.coaching .muted, .athlete-home .muted). This
                  markup used to live under .coaching and would not have taken
                  that rule with it, so the week line would have arrived on /home
                  the same colour as the athlete's name.
                */}
                {week && <div className="meta-copy roster-week">{week}</div>}
              </span>
              <span className="roster-inline">
                {/* Beside the adherence badges, because it answers the same
                    question from the other side: those say what they have
                    done, this says what they turned up with. */}
                {readiness && (
                  <span className={`roster-badge roster-badge-readiness-${readiness.tone}`}>
                    {readiness.text}
                  </span>
                )}
                {row.dueToday > 0 && (
                  <span className="roster-badge roster-badge-due">
                    {row.dueToday} due
                  </span>
                )}
                {row.overdue > 0 && (
                  <span className="roster-badge roster-badge-overdue">
                    {row.overdue} overdue
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default RosterOverview;
